import { ThemeMode, TenantThemeModeOverrides, TenantThemePayload } from 'src/app/base/services/theme.model';
import { ThemeTokenPreviewSnapshot, emptyThemeTokenPreviewSnapshot } from './theme-token-override.util';

export interface TenantThemeSnapshot {
  tenantPrimary: string;
  tenantSecondary: string;
  tenantLogoUrl: string;
  tenantLogoUrlDarkMode: string;
}

export function emptyTenantThemeSnapshot(): TenantThemeSnapshot {
  return { tenantPrimary: '', tenantSecondary: '', tenantLogoUrl: '', tenantLogoUrlDarkMode: '' };
}

export function captureTenantThemeSnapshot(document: Document): TenantThemeSnapshot {
  const rootElement = document.documentElement;
  return {
    tenantPrimary: rootElement.style.getPropertyValue('--tenant-primary'),
    tenantSecondary: rootElement.style.getPropertyValue('--tenant-secondary'),
    tenantLogoUrl: rootElement.style.getPropertyValue('--tenant-logo-url'),
    tenantLogoUrlDarkMode: rootElement.style.getPropertyValue('--tenant-logo-url-dark')
  };
}

export function restoreTenantThemeSnapshot(document: Document, snapshot: TenantThemeSnapshot): void {
  const rootElement = document.documentElement;
  restoreCssVariable(rootElement, '--tenant-primary', snapshot.tenantPrimary);
  restoreCssVariable(rootElement, '--tenant-secondary', snapshot.tenantSecondary);
  restoreCssVariable(rootElement, '--tenant-logo-url', snapshot.tenantLogoUrl);
  restoreCssVariable(rootElement, '--tenant-logo-url-dark', snapshot.tenantLogoUrlDarkMode);
}

export function applyTenantThemePreview(document: Document, payload: TenantThemePayload | null, mode: ThemeMode): void {
  const rootElement = document.documentElement;

  applyOptionalThemeColor(rootElement, '--tenant-primary', payload?.primary);
  applyOptionalThemeColor(rootElement, '--tenant-secondary', payload?.secondary);

  const logoUrl = String(payload?.logoUrl ?? '').trim();
  const logoUrlDarkMode = String(payload?.logoUrlDarkMode ?? '').trim();

  if (logoUrlDarkMode) {
    rootElement.style.setProperty('--tenant-logo-url-dark', `url("${logoUrlDarkMode}")`);
  } else {
    rootElement.style.removeProperty('--tenant-logo-url-dark');
  }

  const resolvedLogoUrl = mode === 'dark' && logoUrlDarkMode ? logoUrlDarkMode : logoUrl;
  if (resolvedLogoUrl) {
    rootElement.style.setProperty('--tenant-logo-url', `url("${resolvedLogoUrl}")`);
  } else {
    rootElement.style.removeProperty('--tenant-logo-url');
  }
}

export function captureThemeTokenPreviewSnapshot(document: Document): ThemeTokenPreviewSnapshot {
  const styleElement = document.getElementById('tenant-theme-overrides');
  if (!(styleElement instanceof HTMLStyleElement)) {
    return emptyThemeTokenPreviewSnapshot();
  }

  return {
    light: readDeclarationsFromSelector(styleElement, "[data-theme='light']"),
    dark: readDeclarationsFromSelector(styleElement, "[data-theme='dark']")
  };
}

export function buildOverridesFromSnapshot(snapshot: ThemeTokenPreviewSnapshot): TenantThemeModeOverrides {
  return {
    light: { ...snapshot.light },
    dark: { ...snapshot.dark }
  };
}

export function extractLogoUrl(cssLogoValue: string): string {
  const trimmedValue = cssLogoValue.trim();
  if (!trimmedValue) {
    return '';
  }

  const regexMatch = trimmedValue.match(/^url\(["']?(.*?)["']?\)$/i);
  return regexMatch ? regexMatch[1] : trimmedValue;
}

export function getValidatedHexColor(source: string | null | undefined): string | undefined {
  const value = String(source ?? '').trim();
  return isHexColor(value) ? value : undefined;
}

export function isHexColor(value: unknown): boolean {
  return typeof value === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value.trim());
}

function applyOptionalThemeColor(rootElement: HTMLElement, cssVariable: string, value: string | null | undefined): void {
  const color = String(value ?? '').trim();

  if (!color) {
    rootElement.style.removeProperty(cssVariable);
    return;
  }

  if (isHexColor(color)) {
    rootElement.style.setProperty(cssVariable, color);
  }
}

function restoreCssVariable(rootElement: HTMLElement, cssVariable: string, value: string): void {
  const normalizedValue = value?.trim();
  if (normalizedValue) {
    rootElement.style.setProperty(cssVariable, normalizedValue);
  } else {
    rootElement.style.removeProperty(cssVariable);
  }
}

function readDeclarationsFromSelector(styleElement: HTMLStyleElement, selector: string): Record<string, string> {
  const content = styleElement.textContent ?? '';
  const escapedSelector = selector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const selectorRegex = new RegExp(`${escapedSelector}\\s*\\{([^}]*)\\}`, 'm');
  const selectorMatch = content.match(selectorRegex);

  if (!selectorMatch?.[1]) {
    return {};
  }

  const declarations = selectorMatch[1].split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const separatorIndex = entry.indexOf(':');
      if (separatorIndex < 0) {
        return null;
      }

      const key = entry.substring(0, separatorIndex).trim();
      const value = entry.substring(separatorIndex + 1).trim();
      return key && value ? [key, value] as const : null;
    })
    .filter((entry): entry is readonly [string, string] => Boolean(entry));

  return Object.fromEntries(declarations);
}
